import React from "react";
import { Check } from "lucide-react";

/**
 * Dynamic EAV field renderer for Step 2 of the caregiver registration/edit form.
 * 
 *
 * Props:
 *  - categoryFields: CategoryField[] from GET /api/admin/service-categories/:id
 *  - fieldValues: { [fieldId]: string[] } — current values keyed by field.id
 *  - setFieldValues: (updater) => void
 *  - errors: { [fieldId]: string } — optional field-level errors
 */
const Step2ServiceDetails = ({
  categoryFields = [],
  fieldValues = {},
  setFieldValues,
  categoryLabel = "Service",
  errors = {},
}) => {
  const getValue = (fieldId) => fieldValues[fieldId] ?? [];

  const setSingle = (fieldId, value) =>
    setFieldValues((prev) => ({ ...prev, [fieldId]: [value] }));

  const toggleMulti = (fieldId, value) =>
    setFieldValues((prev) => {
      const current = prev[fieldId] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [fieldId]: next };
    });

  const setMultiSelect = (fieldId, e) => {
    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
    setFieldValues((prev) => ({ ...prev, [fieldId]: selected }));
  };

  if (categoryFields.length === 0) {
    return (
      <div className="animate-in fade-in slide-in-from-right-4 duration-300">
        <h2 className="text-xl font-bold text-text dark:text-dark-text mb-6 flex items-center gap-2">
          <span className="w-1 h-6 bg-primary rounded-full" />
          {categoryLabel} Details
        </h2>
        <div className="flex items-center justify-center py-12 text-gray-400 dark:text-gray-600 text-sm">
          No additional fields configured for this category.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h2 className="text-xl font-bold text-text dark:text-dark-text mb-6 flex items-center gap-2">
        <span className="w-1 h-6 bg-primary rounded-full" />
        {categoryLabel} Details
      </h2>

      <div className="space-y-6">
        {categoryFields.map((field) => {
          const val = getValue(field.id);
          const err = errors[field.id];

          return (
            <div key={field.id}>
              <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                {field.label}
                {field.isRequired && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              {field.helpText && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                  {field.helpText}
                </p>
              )}

              {/* TEXT */}
              {field.inputType === "TEXT" && (
                <input
                  type="text"
                  value={val[0] ?? ""}
                  onChange={(e) => setSingle(field.id, e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                />
              )}

              {/* NUMBER */}
              {field.inputType === "NUMBER" && (
                <input
                  type="number"
                  value={val[0] ?? ""}
                  onChange={(e) => setSingle(field.id, e.target.value)}
                  min="0"
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                />
              )}

              {/* RADIO */}
              {field.inputType === "RADIO" && (
                <div className="flex flex-wrap gap-3">
                  {(field.options ?? []).map((opt) => {
                    const selected = val[0] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSingle(field.id, opt.value)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${selected
                            ? "bg-primary text-white border-primary shadow-sm"
                            : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary/50"
                          }`}
                      >
                        {selected && <Check size={12} className="inline mr-1.5 mb-0.5" />}
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* DROPDOWN */}
              {field.inputType === "DROPDOWN" && (
                <select
                  value={val[0] ?? ""}
                  onChange={(e) => setSingle(field.id, e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                >
                  <option value="">Select {field.label}</option>
                  {(field.options ?? []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}

              {/* CHECKBOX */}
              {field.inputType === "CHECKBOX" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(field.options ?? []).map((opt) => {
                    const checked = val.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleMulti(field.id, opt.value)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-all ${checked
                            ? "bg-primary/10 border-primary text-primary font-medium"
                            : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary/40"
                          }`}
                      >
                        <span
                          className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${checked
                              ? "bg-primary border-primary"
                              : "border-gray-300 dark:border-gray-600"
                            }`}
                        >
                          {checked && <Check size={10} className="text-white" />}
                        </span>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* MULTI_SELECT */}
              {field.inputType === "MULTI_SELECT" && (
                <select
                  multiple
                  value={val}
                  onChange={(e) => setMultiSelect(field.id, e)}
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm min-h-[100px]"
                >
                  {(field.options ?? []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}

              {err && (
                <p className="text-red-500 text-xs mt-1">{err}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Step2ServiceDetails;
